import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "react-router-dom";

export const AlertsBell = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    load();
    if (!user) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, load]);

  const unread = alerts.filter((a) => !a.read).length;

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    load();
  };

  const markAll = async () => {
    await api.post("/alerts/mark-all-read");
    load();
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-[var(--surface)] transition-colors"
          data-testid="alerts-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-[var(--teak)]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--primary)] text-white text-[10px] font-bold flex items-center justify-center" data-testid="alerts-badge">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="alerts-dropdown">
        <div className="flex items-center justify-between p-3 border-b border-[var(--line)]">
          <div className="font-serif text-base text-[var(--teak)]">Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[var(--primary)]" data-testid="mark-all-read">Mark all read</button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="p-6 text-center text-sm text-[var(--ink-muted)]">No notifications yet</div>
          ) : alerts.map((a) => (
            <Link
              key={a.id}
              to={a.property_id ? `/property/${a.property_id}` : "#"}
              onClick={() => { markRead(a.id); setOpen(false); }}
              className={`block p-3 border-b border-[var(--line)] hover:bg-[var(--surface)] ${a.read ? "" : "bg-[rgba(200,90,50,0.04)]"}`}
              data-testid={`alert-item-${a.id}`}
            >
              <div className="flex items-start gap-2">
                {!a.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[var(--primary)] shrink-0" />}
                <div className="flex-1">
                  <div className="text-sm font-medium text-[var(--teak)]">{a.title}</div>
                  <div className="text-xs text-[var(--ink-muted)] mt-0.5">{a.message}</div>
                  <div className="text-[10px] text-[var(--ink-muted)] uppercase tracking-wider mt-1">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
